import React, { useEffect, useState } from "react";
import { Bookmark, X } from "lucide-react";

// FirstTimeBookmarkTooltip — fires ONCE per user, on their 3rd
// distinct video view. Points at the bookmark icon in the comments
// panel header (data-testid='feed-trail-toggle') and explains the
// Save-to-Trail feature.
//
// Why a tooltip vs. an onboarding tour:
//   - One-shot, non-blocking, dismissable.
//   - Surfaces feature discovery WITHOUT interrupting playback.
//   - Designed to feel like an iOS hint, not a desktop modal.
//
// Persistence model: localStorage key `globaleyes:bookmark_hint_seen`.
// We set it on either explicit dismiss OR after the user taps the
// bookmark (proof they found it without help). No expiry — once shown
// AND understood, never shown again.
//
// Trigger logic owned by parent (FeedPage / ProfilePage call
// `<FirstTimeBookmarkTooltip videoViews={n} />` and we self-decide
// when to render). We use videoViews>=3 so first-time visitors aren't
// hit with a tooltip before they've even watched anything.
const STORAGE_KEY = "globaleyes:bookmark_hint_seen";

const FirstTimeBookmarkTooltip = ({ videoViews = 0, user = null }) => {
  const [visible, setVisible] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  useEffect(() => {
    // Gating: only logged-in users (anonymous viewers can't save), at
    // least 3 videos viewed, and never seen the hint before.
    if (!user) return;
    if (videoViews < 3) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch (e) { return; /* private-browsing — skip */ }

    // Find the bookmark button — defer one tick so the player overlay
    // has time to render the panel after isPlaying flips true.
    const t = setTimeout(() => {
      const btn = document.querySelector('[data-testid="feed-trail-toggle"]');
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setAnchorRect(rect);
      setVisible(true);

      // Also auto-dismiss when the user taps the bookmark — they
      // found it without us. Listen once.
      const onTap = () => {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) {}
        setVisible(false);
        btn.removeEventListener("click", onTap);
      };
      btn.addEventListener("click", onTap, { once: true });
      return () => btn.removeEventListener("click", onTap);
    }, 400);
    return () => clearTimeout(t);
  }, [videoViews, user]);

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) {}
    setVisible(false);
  };

  if (!visible || !anchorRect) return null;

  // Position the tooltip BELOW the anchor with a small arrow pointing
  // up. Clamp to viewport edges so it never spills offscreen on
  // narrow phones.
  const tooltipWidth = 240;
  const left = Math.max(
    12,
    Math.min(
      window.innerWidth - tooltipWidth - 12,
      anchorRect.left + anchorRect.width / 2 - tooltipWidth / 2
    )
  );
  const top = anchorRect.bottom + 10;

  return (
    <div
      data-testid="first-time-bookmark-tooltip"
      className="fixed z-[1100] animate-in fade-in slide-in-from-top-2"
      style={{ left, top, width: tooltipWidth }}
    >
      {/* Up-arrow caret pointing at the bookmark. Inline-absolute so
          it tracks with the tooltip body and survives viewport edge
          clamping. */}
      <div
        className="absolute -top-1.5 w-3 h-3 bg-amber-400 rotate-45 border-l border-t border-amber-300"
        style={{
          left: Math.max(12, Math.min(tooltipWidth - 18, anchorRect.left + anchorRect.width / 2 - left - 6)),
        }}
      />
      <div className="relative bg-amber-400 text-gray-900 rounded-lg px-3 py-2.5 shadow-2xl border border-amber-300">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-1.5 right-1.5 p-0.5 rounded-full hover:bg-amber-500/40 transition-colors"
          aria-label="Dismiss"
          data-testid="bookmark-tooltip-dismiss"
        >
          <X className="h-3 w-3" />
        </button>
        <div className="flex items-start gap-2 pr-4">
          <Bookmark className="h-4 w-4 mt-0.5 shrink-0 fill-gray-900" />
          <div className="text-[12px] leading-snug">
            <p className="font-bold mb-0.5">Save videos you love</p>
            <p className="opacity-90">
              Tap the bookmark to add a video to your personal Trail —
              and the creator sees it as social proof.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeBookmarkTooltip;
